/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { dataEqual } from "@adapptio/lib-blueprint";

import {
	createEventEmitter,
	emitEvent,
	removeAllEventListeners
} from "@adapptio/lib-shared";

import { IEditorApiService } from "../api/IEditorApiService";
import { BrowserLogger } from "../common/BrowserLogger";
import { IEditor } from "../IEditor";
import { IEditorApiReleaseListResponse, IScmInfo } from "@adapptio/lib-core";

const RELOAD_INTERVAL = 120000;

/**
 * Service to manage source control related operations
 */
export class ScmService {
	/** Logger facility */
	private logger: BrowserLogger;

	/** API Service Instance */
	private apiService: IEditorApiService;

	/* Reload timers */
	private reloadTimer: ReturnType<typeof setTimeout>;

	/* Cached data */
	private scmInfo: IScmInfo | null = null;
	private releaseListData: IEditorApiReleaseListResponse = { releases: [], nextTagName: "" };

	/* Events */
	public onScmInfoChange = createEventEmitter<void>();
	public onReleaseListChange = createEventEmitter<void>();

	/**
	 * Resource Service Constructor
	 *
	 * @param dc Dependency Container
	 */
	public constructor(dc: IEditor) {
		this.logger = dc.get("logger").facility("DeploymentService");

		this.apiService = dc.get("apiService");
	}

	/**
	 * Initializes service
	 */
	public async init(): Promise<void> {
		this.handleReloadData(true);
	}

	/**
	 * Disposes service
	 */
	public async dispose(): Promise<void> {
		if (this.reloadTimer) {
			clearTimeout(this.reloadTimer);
		}

		removeAllEventListeners(this.onReleaseListChange);
	}

	private handleReloadData(loadStatus = true) {
		if (loadStatus) {
			this.loadReleaseList();
			this.loadScmInfo();
		}

		this.reloadTimer = setTimeout(
			() => this.handleReloadData(),
			RELOAD_INTERVAL
		);
	}

	public async loadScmInfo(): Promise<void> {
		try {
			const data = await this.apiService.getScmInfo();

			if (!dataEqual(data, this.scmInfo)) {
				this.scmInfo = data;
				emitEvent(this.onScmInfoChange);
			}
		} catch (err) {
			this.logger.error("Failed to load SCM info:", err);
		}
	}

	public async loadReleaseList(): Promise<void> {
		try {
			const data = await this.apiService.getReleaseList();

			if (!dataEqual(data, this.releaseListData)) {
				this.releaseListData = data;
				emitEvent(this.onReleaseListChange);
			}
		} catch (err) {
			this.logger.error("Failed to load deployment status:", err);
		}
	}

	/**
	 * Creates a new release
	 * @param tagName Tag name
	 * @param notes Release notes
	 */
	public async createRelease(
		tagName: string,
		notes: string
	) : Promise<void> {
		const tagAlreadyExists = this.releaseListData.releases.some((release => release.tagName === tagName));

		if (tagAlreadyExists) {
			throw new Error(`Tag name "${tagName}" already exists. Please choose a different name.`);
		}

		await this.apiService.createRelease(tagName, notes);
	}

	public getReleaseList(): IEditorApiReleaseListResponse {
		return this.releaseListData;
	}

	public getScmInfo(): IScmInfo | null {
		return this.scmInfo;
	}
}
